package org.example.newportfolio.models

import kotlin.js.Date

/**
 * Represents a single role within a company.
 */
data class ExperienceRole(
    val jobPosition: String,
    val from: String,
    val to: String,
    val description: String,
    val skills: List<String>,
    /** Optional project thumbnails shown under this role (image path + link + label) */
    val projects: List<ExperienceProject> = emptyList(),
)

data class ExperienceProject(
    val name: String,
    val imageRes: String,
    val link: String,
)

/**
 * Top-level experience entry. Some companies (like Inside Tech) contain multiple
 * sub-roles that should be displayed together under one header.
 */
enum class Experience(
    val number: String,
    val company: String,
    val employmentType: String,
    val location: String,
    val roles: List<ExperienceRole>,
    /** Optional review/testimonial screenshots (freelance section) */
    val reviewImages: List<String> = emptyList(),
) {
    First(
        number = "01",
        company = "Apps Square",
        employmentType = "Full-time",
        location = "Hybrid",
        roles = listOf(
            ExperienceRole(
                jobPosition = "Mobile Developer",
                from = "DEC 2025",
                to = "PRESENT",
                description = "Designing and developing advanced Mobile applications using Flutter, Kotlin, and Jetpack Compose.\n" +
                        "Implementing Clean Architecture and MVVM patterns to ensure code maintainability and testability.\n" +
                        "Collaborating with cross-functional teams to define, design, and ship new features.\n" +
                        "Ensuring application robustness through Unit Testing and continuous performance optimization.\n" +
                        "Utilizing modern Android libraries and RESTful APIs to deliver seamless user experiences.",
                skills = listOf("Flutter", "Dart", "Kotlin", "Jetpack Compose", "KMP", "XML", "Coroutines", "Flow", "Hilt", "Retrofit", "Room", "Git")
            )
        )
    ),
    Second(
        number = "02",
        company = "Freelance (Upwork)",
        employmentType = "Freelance",
        location = "Remote",
        roles = listOf(
            ExperienceRole(
                jobPosition = "Mobile Application Developer",
                from = "SEP 2025",
                to = "PRESENT",
                description = "Turning innovative ideas into high-quality mobile reality with clear communication and timely delivery.\n" +
                        "100% Job Success score on Upwork with consistently high client satisfaction ratings.\n" +
                        "Delivering end-to-end mobile solutions from architecture design to deployment on App Store and Google Play.\n" +
                        "Specializing in Flutter, Kotlin, and Jetpack Compose for cross-platform and native Android development.",
                skills = listOf("Mobile Apps", "Flutter", "Kotlin", "Jetpack Compose", "SDLC", "Communication", "Timely Delivery")
            )
        ),
        reviewImages = listOf(
            "images/freelance_review_1.webp",
            "images/freelance_review_2.webp",
            "images/freelance_review_3.webp"
        )
    ),
    Third(
        number = "03",
        company = "Inside Tech",
        employmentType = "Full-time",
        location = "Jordan · Remote",
        roles = listOf(
            ExperienceRole(
                jobPosition = "Mobile Application Developer",
                from = "APR 2025",
                to = "NOV 2025",
                description = "Developing two production-ready mobile applications using Kotlin Multiplatform, Flutter, and Jetpack Compose, applying Clean Architecture and reusable UI components.\n" +
                        "Delivering scalable features in collaboration with backend/design teams.\n" +
                        "Applied Clean Architecture and created reusable UI components.\n" +
                        "Collaborated in Agile sprints with backend developers to ensure smooth integrations.",
                skills = listOf("Kotlin Multiplatform", "Flutter", "Compose", "Clean Architecture", "Agile")
            ),
            ExperienceRole(
                jobPosition = "Android Developer",
                from = "NOV 2024",
                to = "MAR 2025",
                description = "Building and maintaining high-performance Mobile apps using Kotlin, MVVM, Jetpack Components, and Retrofit.\n" +
                        "Agile Collaboration: Worked with backend teams using Agile methodologies to ensure timely delivery and client satisfaction.\n" +
                        "Feature Integration: Developed and integrated key features such as room booking, staff management, and reporting into a Hotel Management System UI using Jetpack Compose.\n" +
                        "Performance Optimization: Improved app speed by 25% through advanced caching and data synchronization techniques.\n" +
                        "Ensuring code quality with unit testing (JUnit, MockK, Espresso) and optimizing app performance.",
                skills = listOf("Android", "Kotlin", "MVVM", "Retrofit", "JUnit", "MockK", "Espresso", "Jetpack Compose", "Coroutines"),
                projects = listOf(
                    ExperienceProject(
                        name = "Hotel X",
                        imageRes = "images/hotel.webp",
                        link = "https://youtu.be/SLVSWdPvxeQ"
                    )
                )
            )
        )
    ),
    Fourth(
        number = "04",
        company = "TAKADOM AL-IRAQ",
        employmentType = "Freelance",
        location = "Remote",
        roles = listOf(
            ExperienceRole(
                jobPosition = "Software Development Engineer in Test",
                from = "AUG 2024",
                to = "AUG 2025",
                description = "Engineered scalable automation solutions for Mobile Applications and RESTful APIs using Kotlin as the primary language.\n" +
                        "Built API testing frameworks using Kotlin to verify backend endpoints, data integrity, and response performance.\n" +
                        "Applied OOP principles and Clean Code practices to write maintainable and reusable test code, mirroring production-level development standards.\n" +
                        "Collaborated with the development team to identify edge cases and debug issues early in the software lifecycle.\n" +
                        "Successfully managed full-time remote responsibilities while fulfilling mandatory military service, demonstrating exceptional discipline and time management.",
                skills = listOf("Testing Process", "SDLC", "Kotlin", "API Testing", "Clean Code", "OOP", "Automation"),
                projects = listOf(
                    ExperienceProject(
                        name = "كوبوني",
                        imageRes = "images/couponi_logo.webp",
                        link = "https://play.google.com/store/apps/details?id=iq.elitsoft.couponi.user&hl=en_US"
                    ),
                    ExperienceProject(
                        name = "نفطكم",
                        imageRes = "images/naftcom_logo.webp",
                        link = "https://play.google.com/store/apps/details?id=iq.elitesoft.naftcom.citizens&hl=en_US"
                    ),
                    ExperienceProject(
                        name = "نفطكم - الموظف",
                        imageRes = "images/naftcom_employee_logo.webp",
                        link = "https://play.google.com/store/apps/details?id=iq.elitesoft.naftcom.station&hl=en_US"
                    )
                )
            )
        )
    ),
    Fifth(
        number = "05",
        company = "The Chance",
        employmentType = "Internship",
        location = "Remote",
        roles = listOf(
            ExperienceRole(
                jobPosition = "Android Developer",
                from = "FEB 2023",
                to = "SEP 2023",
                description = "Mastered Android Development: Honed skills in Kotlin, OOP, Git, Design Patterns, Compose, Android SDK, and advanced Android concepts over eight months.\n" +
                        "Excelled in design patterns and architectural paradigms such as MVC, MVP, MVVM, and Clean Architecture.\n" +
                        "Effective Team Leadership: Successfully led three projects, showcasing strong leadership and project management abilities.\n" +
                        "Enabled efficient collaboration and drove projects to completion.",
                skills = listOf("Android SDK", "Kotlin", "Compose", "MVC", "MVP", "MVVM", "Clean Architecture", "Animation", "Data Structures", "Design Patterns", "Leadership", "Git")
            )
        )
    ),
    Sixth(
        number = "06",
        company = "egFWD",
        employmentType = "Internship",
        location = "Remote",
        roles = listOf(
            ExperienceRole(
                jobPosition = "Android Developer",
                from = "SEP 2022",
                to = "DEC 2022",
                description = "Acquired advanced skills in contemporary Android app development during Udacity Advanced Android Kotlin Development Nanodegree.\n" +
                        "Actively developed Android projects utilizing the latest tools and technologies.\n" +
                        "Mastered UI design, seamless RESTful API integration, and robust MVVM architecture, resulting in user-centric and responsive projects.",
                skills = listOf("Kotlin", "Coroutines", "Flow", "REST API", "UI Design", "MVVM")
            )
        )
    );

    /** Computed total duration across all roles */
    val totalDuration: String get() {
        val allFrom = roles.map { it.from }
        val allTo = roles.map { it.to }
        // Earliest start
        val earliest = allFrom.mapNotNull { parseMonthYear(it) }.minByOrNull { it.second * 12 + it.first } ?: return ""
        // Latest end
        val latest = allTo.map {
            if (it.equals("PRESENT", ignoreCase = true)) nowMonthYear() else parseMonthYear(it) ?: return ""
        }.maxByOrNull { it.second * 12 + it.first } ?: return ""
        val months = (latest.second * 12 + latest.first) - (earliest.second * 12 + earliest.first) + 1
        return formatMonths(if (months < 0) 0 else months)
    }

    companion object {
        private val monthMap = mapOf(
            "JAN" to 1, "FEB" to 2, "MAR" to 3, "APR" to 4, "MAY" to 5, "JUN" to 6,
            "JUL" to 7, "AUG" to 8, "SEP" to 9, "OCT" to 10, "NOV" to 11, "DEC" to 12,
            "Jan" to 1, "Feb" to 2, "Mar" to 3, "Apr" to 4, "May" to 5, "Jun" to 6,
            "Jul" to 7, "Aug" to 8, "Sep" to 9, "Oct" to 10, "Nov" to 11, "Dec" to 12
        )

        fun parseMonthYear(raw: String): Pair<Int, Int>? {
            val parts = raw.trim().split(" ")
            if (parts.size != 2) return null
            val month = monthMap[parts[0]] ?: return null
            val year = parts[1].toIntOrNull() ?: return null
            return month to year
        }

        fun nowMonthYear(): Pair<Int, Int> {
            val now = Date()
            return (now.getMonth() + 1) to now.getFullYear().toInt()
        }

        fun computeTotalMonths(from: String, to: String): Int {
            val (fromMonth, fromYear) = parseMonthYear(from) ?: return 0
            val (toMonth, toYear) = if (to.equals("PRESENT", ignoreCase = true)) nowMonthYear() else parseMonthYear(to) ?: return 0
            val diff = (toYear * 12 + toMonth) - (fromYear * 12 + fromMonth) + 1
            return if (diff < 0) 0 else diff
        }

        fun formatMonths(total: Int): String {
            if (total <= 0) return "0 months"
            val years = total / 12
            val months = total % 12
            val yearPart = when (years) { 0 -> null; 1 -> "1 yr"; else -> "$years yrs" }
            val monthPart = when (months) { 0 -> null; 1 -> "1 mo"; else -> "$months mos" }
            return listOfNotNull(yearPart, monthPart).joinToString(" ")
        }

        fun formatRoleDuration(from: String, to: String): String {
            val months = computeTotalMonths(from, to)
            return formatMonths(months)
        }
    }
}
